import { CollectionMethod } from '../retail-customer-configuration';
import {
  RecurringInvoiceCustomerAddress as ApiRecurringInvoiceCustomerAddressApi,
  RecurringInvoice as RecurringInvoiceApi,
  RecurringInvoiceItem as RecurringInvoiceItemApi,
  RecurringInvoiceLineItem as RecurringInvoiceLineItemApi,
  RecurringInvoiceBundle as RecurringInvoiceBundleApi,
  RecurringInvoiceLineItemInterface,
  RecurringInvoiceBundleInterface,
} from '@vendasta/billing';
import { convertFromApiCollectionMethod } from './collection-method';
import { convertFromApiInterval, Interval } from './interval';
import { convertFromApiPaymentProcessor, PaymentProcessor } from './payment-processor';
import { convertFromApiRecurringInvoiceStatus, RecurringInvoiceStatus } from './status';

export class ListRecurringInvoicesByCustomerID {
  customerId: string;

  constructor(customerId: string) {
    this.customerId = customerId;
  }
}

export class ListRecurringInvoicesByMerchantID {
  merchantId: string;

  constructor(merchantId: string) {
    this.merchantId = merchantId;
  }
}

export interface ListRecurringInvoicesFilters {
  statuses?: RecurringInvoiceStatus[];
  detailedInterval?: ListRecurringInvoicesDetailedInterval[];
  dateFilter?: ListRecurringInvoicesDateFilter;
}

export interface ListRecurringInvoicesDetailedInterval {
  interval: Interval;
  intervalCount: number;
}
export interface ListRecurringInvoicesDateFilter {
  dateGte?: Date;
  dateLte?: Date;
}

export interface CreateRecurringInvoiceRequest {
  customerId: string;
  nextIssue: Date;
  intervalCount: number;
  interval: Interval;
  repeatOnDayOfMonth?: number;
  collectionMethod: CollectionMethod;
  recipientUserId: string;
  additionalRecipientIds: string[];
  paymentProcessorId: string;
  paymentProcessor: PaymentProcessor;
  items: (RecurringInvoiceLineItem | RecurringInvoiceBundle)[];
  status: RecurringInvoiceStatus;
  memo?: string;
  endsAt?: Date;
  netD?: number;
}
class LineItem {
  name: string;

  constructor(name: string) {
    this.name = name;
  }
}

export const isRecurringInvoiceLineItem = (
  li: RecurringInvoiceLineItem | undefined,
): li is RecurringInvoiceLineItem => {
  return !!li;
};
export class RecurringInvoiceLineItem extends LineItem implements RecurringInvoiceLineItemInterface {
  sku?: string;
  unitPrice: number;
  quantity: number;
  taxRateIds?: string[];

  constructor(kwargs?: RecurringInvoiceLineItemInterface) {
    super(kwargs?.name || '');
    this.sku = kwargs?.sku || '';
    this.unitPrice = kwargs?.unitPrice || 0;
    this.quantity = kwargs?.quantity || 0;
    this.taxRateIds = kwargs?.taxRateIds || [];
  }
}

// DEPRECATED: a bundle is now represented as single item on the invoice rather than it being comprised of all individual products within said bundle.
// New recurring invoices will not be created with bundle items, however, we still need to support the class to maintain the legacy bundle view so that we do not
// break the historical representation of past recurring invoices.
export class RecurringInvoiceBundle extends LineItem implements RecurringInvoiceBundleInterface {
  hideLineItems: boolean;
  lineItems: RecurringInvoiceLineItem[];

  constructor(kwargs?: RecurringInvoiceBundleInterface) {
    super(kwargs?.name || '');
    this.hideLineItems = kwargs?.hideLineItems || false;
    this.lineItems = (kwargs?.lineItems || []).map((li) => new RecurringInvoiceLineItem(li));
  }
}

export interface RecurringInvoiceCustomerAddress {
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

function convertApiCustomerAddress(ca: ApiRecurringInvoiceCustomerAddressApi): RecurringInvoiceCustomerAddress {
  return {
    address: ca?.address || '',
    address2: ca?.address2 || '',
    city: ca?.city || '',
    state: ca?.state || '',
    zip: ca?.zip || '',
    country: ca?.country || '',
  };
}

export interface RecurringInvoice {
  id: string;
  created?: Date;
  updated?: Date;
  customerId: string;
  customerName: string;
  status: RecurringInvoiceStatus;
  nextIssue: Date;
  intervalCount: number;
  interval: Interval;
  repeatOnDayOfMonth?: number;
  collectionMethod: CollectionMethod;
  recipientUserId: string;
  additionalRecipientIds: string[];
  paymentProcessor: PaymentProcessor;
  paymentProcessorId: string;
  items: (RecurringInvoiceLineItem | RecurringInvoiceBundle)[];
  totalAmount?: number;
  currencyCode?: string;
  memo?: string;
  endsAt?: Date;
  customerAddress: RecurringInvoiceCustomerAddress;
  netD?: number;
}

function convertFromApiRecurringInvoiceLineItem(li: RecurringInvoiceLineItemApi): RecurringInvoiceLineItem | undefined {
  if (!li) {
    return undefined;
  }
  return new RecurringInvoiceLineItem({
    sku: li.sku,
    unitPrice: li.unitPrice,
    quantity: li.quantity,
    name: li.name,
    taxRateIds: li.taxRateIds || [],
  });
}

function convertFromApiRecurringInvoiceLineItemBundle(
  li: RecurringInvoiceBundleApi,
): RecurringInvoiceBundle | undefined {
  if (!li) {
    return undefined;
  }
  return new RecurringInvoiceBundle({
    name: li.name,
    hideLineItems: li.hideLineItems || false,
    lineItems: (li.lineItems || [])
      .map((i) => convertFromApiRecurringInvoiceLineItem(i))
      .filter(isRecurringInvoiceLineItem),
  });
}

function convertFromApiRecurringInvoiceItem(
  i: RecurringInvoiceItemApi,
): RecurringInvoiceLineItem | RecurringInvoiceBundle | undefined {
  if (!!i.lineItem === true) {
    return convertFromApiRecurringInvoiceLineItem(i.lineItem);
  } else if (!!i.bundle === true) {
    return convertFromApiRecurringInvoiceLineItemBundle(i.bundle);
  }
  return undefined;
}

export function convertFromApiRecurringInvoice(i: RecurringInvoiceApi): RecurringInvoice {
  const zeroDate = new Date('0001-01-01T00:00:00Z').valueOf();
  return {
    id: i.id,
    created: i.created.valueOf() !== zeroDate ? i.created : null,
    updated: i.updated.valueOf() !== zeroDate ? i.updated : null,
    customerId: i.customerId,
    customerName: i.customerName || '',
    status: convertFromApiRecurringInvoiceStatus(i.status),
    nextIssue: i.nextIssue.valueOf() !== zeroDate ? i.nextIssue : null,
    intervalCount: i.intervalCount,
    interval: convertFromApiInterval(i.interval),
    repeatOnDayOfMonth: i.repeatOnDayOfMonth,
    collectionMethod: convertFromApiCollectionMethod(i.collectionMethod),
    recipientUserId: i.recipientUserId,
    additionalRecipientIds: i.additionalRecipientIds || [],
    paymentProcessor: convertFromApiPaymentProcessor(i.paymentProcessor),
    paymentProcessorId: i.paymentProcessorId,
    items: (i.items || []).map((li) => convertFromApiRecurringInvoiceItem(li)),
    totalAmount: i.totalAmount || 0,
    currencyCode: i.currencyCode,
    memo: i.memo,
    endsAt: i?.endsAt?.valueOf() !== zeroDate ? i.endsAt : null,
    customerAddress: convertApiCustomerAddress(i.customerAddress),
    netD: i.netD || 7,
  } as RecurringInvoice;
}

function convertToApiRecurringInvoiceLineItem(li: RecurringInvoiceLineItem): RecurringInvoiceLineItemApi | undefined {
  if (!li) {
    return undefined;
  }
  return new RecurringInvoiceLineItemApi({
    sku: li.sku,
    unitPrice: li.unitPrice,
    quantity: li.quantity,
    name: li.name,
    taxRateIds: li.taxRateIds,
  });
}

const isRecurringInvoiceLineItemApi = (
  li: RecurringInvoiceLineItemApi | undefined,
): li is RecurringInvoiceLineItemApi => {
  return !!li;
};
export function convertToApiRecurringInvoiceItem(
  li: RecurringInvoiceLineItem | RecurringInvoiceBundle,
): RecurringInvoiceItemApi {
  let lineItem: RecurringInvoiceLineItemApi | undefined;
  let bundle: RecurringInvoiceBundleApi | undefined;

  if (li instanceof RecurringInvoiceLineItem) {
    lineItem = convertToApiRecurringInvoiceLineItem(li);
  } else if (li instanceof RecurringInvoiceBundle) {
    bundle = new RecurringInvoiceBundleApi({
      name: li.name,
      hideLineItems: li.hideLineItems,
      lineItems: (li.lineItems || [])
        .map((item) => convertToApiRecurringInvoiceLineItem(item))
        .filter(isRecurringInvoiceLineItemApi),
    });
  }

  return new RecurringInvoiceItemApi({
    lineItem,
    bundle,
  });
}
